import _extends from "@babel/runtime/helpers/extends";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { string, number, shape, bool } from 'prop-types';
import { Link } from 'react-router-dom';
import { UNCONSTRAINED_SIZE_KEY } from '@magento/peregrine/lib/talons/Image/useImage';
import { useGalleryItem } from '@magento/peregrine/lib/talons/Gallery/useGalleryItem';
import resourceUrl from '@magento/peregrine/lib/util/makeUrl';
import { useWindowSize } from '@magento/peregrine/lib/hooks/useWindowSize';
import { useUserContext } from '@magento/peregrine/lib/context/user/';
import Price from '@magento/venia-ui/lib/components/Price';
import { Image, Button } from '..';
import GalleryItemShimmer from '@magento/venia-ui/lib/components/Gallery/item.shimmer';
import classes from './productItem.module.css';
import WishlistGalleryButton from 'src/overrides/venia-ui/components/Wishlist/AddToListPdpButton';
import SignInModal from '../Favorites/SignInModal';
import DownloadImage from '../../assets/download-btn-plp.svg';
import DownloadImageFull from '../../assets/download-full-btn-plp.svg';
import { useMutation } from '@apollo/client';
import { ADD_ITEM_TO_DOWNLOAD, REMOVE_ITEM_FROM_DOWNLOAD } from '../../graphql/downloadImages/actions.gql';
import LoadingIndicator from '@magento/venia-ui/lib/components/LoadingIndicator';
import { FormattedMessage } from 'react-intl';
import { useGalleryItemCollection } from './useGalleryItemCollection';
import useToast from '../../hooks/useToast';
import { useAppContext } from '@magento/peregrine/lib/context/app';
import MiddleDialog from '../Favorites/MiddleDialog';
import { isOutletSku } from '../../hooks/utils';
import OutletTags from '../OutletTags'; // The placeholder image is 4:5, so we should make sure to size our product
// images appropriately.

var IMAGE_WIDTH = 300;
var IMAGE_HEIGHT = 375; // Gallery switches from two columns to three at 640px.

var IMAGE_WIDTHS = new Map().set(640, IMAGE_WIDTH).set(UNCONSTRAINED_SIZE_KEY, 840);

var checkIfValidValue = function checkIfValidValue(value) {
  return value && value !== 'NaN';
};

export var getDimensions = function getDimensions(item) {
  return "".concat(checkIfValidValue(item === null || item === void 0 ? void 0 : item.hf_width) ? "".concat(item === null || item === void 0 ? void 0 : item.hf_width, "\"").concat(checkIfValidValue(item.hf_height) || checkIfValidValue(item.hf_depth_length) ? ' x ' : '') : '').concat(checkIfValidValue(item.hf_height) ? "".concat(item === null || item === void 0 ? void 0 : item.hf_height, "\"").concat(checkIfValidValue(item.hf_depth_length) ? ' x ' : '') : '').concat(checkIfValidValue(item.hf_depth_length) ? "".concat(item === null || item === void 0 ? void 0 : item.hf_depth_length, "\"") : '');
};

var ProductItem = function ProductItem(_ref) {
  var _outletPriceData$prod, _outletPriceData$prod2, _outletPriceData$prod3, _outletPriceData$prod4, _outletPriceData$prod5, _outletPriceData$prod6, _outletPriceData$prod7, _outletPriceData$prod8, _outletPriceData$prod9, _outletPriceData$prod10, _outletPriceData$prod11, _outletPriceData$prod12, _price_range$maximum_, _price_range$maximum_2, _price_range$maximum_3, _price_range$maximum_4;

  var _ref$isCollectionBtnA = _ref.isCollectionBtnAllowed,
      isCollectionBtnAllowed = _ref$isCollectionBtnA === void 0 ? false : _ref$isCollectionBtnA,
      _ref$toggleCollection = _ref.toggleCollection,
      toggleCollection = _ref$toggleCollection === void 0 ? function () {} : _ref$toggleCollection,
      storeConfig = _ref.storeConfig,
      collectionVisible = _ref.collectionVisible,
      item = _ref.item,
      priceRange = _ref.priceRange,
      downloadImages = _ref.downloadImages,
      isDownload = _ref.isDownload,
      isFiltersHidden = _ref.isFiltersHidden,
      refetch = _ref.refetch,
      _ref$handleLinkClick = _ref.handleLinkClick,
      handleLinkClick = _ref$handleLinkClick === void 0 ? function () {} : _ref$handleLinkClick,
      index = _ref.index,
      imageItem = _ref.imageItem,
      productMarkerId = _ref.productMarkerId,
      persistScrollPosition = _ref.persistScrollPosition,
      handleProductScroll = _ref.handleProductScroll,
      isProductScrolled = _ref.isProductScrolled,
      isInFavorites = _ref.isInFavorites,
      refetchFavorites = _ref.refetchFavorites;

  var _useToast = useToast(),
      addMessage = _useToast.addMessage;

  var productItemRef = useRef();

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      modalData = _useState2[0],
      setModalData = _useState2[1];

  var _useState3 = useState(null),
      _useState4 = _slicedToArray(_useState3, 2),
      isWishlistDialogOpen = _useState4[0],
      setIsWishlistDialogOpen = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      isHoveredItem = _useState6[0],
      setIsHoveredItem = _useState6[1];

  var _useWindowSize = useWindowSize(),
      innerWidth = _useWindowSize.innerWidth;

  var isSmallDevice = innerWidth < 980;

  var _useUserContext = useUserContext(),
      _useUserContext2 = _slicedToArray(_useUserContext, 1),
      isUserSignedIn = _useUserContext2[0].isSignedIn;

  var _useAppContext = useAppContext(),
      _useAppContext2 = _slicedToArray(_useAppContext, 2),
      closeDrawer = _useAppContext2[1].closeDrawer;

  var _useState7 = useState(false),
      _useState8 = _slicedToArray(_useState7, 2),
      loading = _useState8[0],
      setLoading = _useState8[1];

  var _useState9 = useState({
    remove: true,
    errorRemoving: false,
    successAdding: false,
    errorAdding: false
  }),
      _useState10 = _slicedToArray(_useState9, 2),
      downloadStatus = _useState10[0],
      setDownloadStatus = _useState10[1];

  var _useGalleryItemCollec = useGalleryItemCollection(),
      handleSubmit = _useGalleryItemCollec.handleSubmit,
      crosssell_products = _useGalleryItemCollec.collectionData,
      crosssell_products_loading = _useGalleryItemCollec.loading;

  useEffect(function () {
    toggleCollection(crosssell_products, index);
  }, [crosssell_products]);

  var _useState11 = useState(null),
      _useState12 = _slicedToArray(_useState11, 2),
      activeItem = _useState12[0],
      setActiveItem = _useState12[1];

  var _useMutation = useMutation(ADD_ITEM_TO_DOWNLOAD),
      _useMutation2 = _slicedToArray(_useMutation, 1),
      addItemToDownloadMethod = _useMutation2[0];

  var _useMutation3 = useMutation(REMOVE_ITEM_FROM_DOWNLOAD),
      _useMutation4 = _slicedToArray(_useMutation3, 1),
      removeItemFromDownloadMethod = _useMutation4[0];

  var _useGalleryItem = useGalleryItem({
    storeConfig: storeConfig,
    item: item
  }),
      wishlistButtonProps = _useGalleryItem.wishlistButtonProps;

  var isOutletProduct = isOutletSku(item === null || item === void 0 ? void 0 : item.sku);
  useEffect(function () {
    if (productMarkerId == null || productMarkerId === undefined) return;else if (productMarkerId === item.id && !isProductScrolled) {
      productItemRef.current.scrollIntoView({
        behavior: 'auto',
        block: 'center'
      });
      handleProductScroll === null || handleProductScroll === void 0 ? void 0 : handleProductScroll();
    } else return;
  }, [productMarkerId]);
  useEffect(function () {
    var clearDownloadStatus = setTimeout(function () {
      return setDownloadStatus({
        remove: true,
        errorRemoving: false,
        successAdding: false,
        errorAdding: false
      });
    }, 3000);
    return function () {
      return clearTimeout(clearDownloadStatus);
    };
  }, []);

  var addItemToDownload = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(sku) {
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              setLoading(true);
              _context.next = 3;
              return addItemToDownloadMethod({
                variables: {
                  input: {
                    product_sku: sku
                  }
                }
              });

            case 3:
              try {
                setDownloadStatus({
                  remove: false,
                  errorRemoving: false,
                  successAdding: true,
                  errorAdding: false
                });
                addMessage('success', 'Successfully added to downloads');
                refetch();
                setLoading(false);
              } catch (error) {
                setLoading(false);
                setDownloadStatus({
                  remove: false,
                  errorRemoving: false,
                  successAdding: false,
                  errorAdding: true
                });
              }

            case 4:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));

    return function addItemToDownload(_x) {
      return _ref2.apply(this, arguments);
    };
  }();

  var removeItemFromDownload = /*#__PURE__*/function () {
    var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(sku) {
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              setLoading(true);
              _context2.next = 3;
              return removeItemFromDownloadMethod({
                variables: {
                  input: {
                    product_sku: sku
                  }
                }
              });

            case 3:
              try {
                refetch();
                setLoading(false);
                setDownloadStatus({
                  remove: false,
                  errorRemoving: false,
                  successAdding: false,
                  errorAdding: false
                });
                addMessage('success', 'Successfully removed from downloads');
              } catch (error) {
                setLoading(false);
                setDownloadStatus({
                  remove: false,
                  errorRemoving: true,
                  successAdding: false,
                  errorAdding: false
                });
              }

            case 4:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2);
    }));

    return function removeItemFromDownload(_x2) {
      return _ref3.apply(this, arguments);
    };
  }();

  var isInDownload = useMemo(function () {
    return downloadImages === null || downloadImages === void 0 ? void 0 : downloadImages.find(function (data) {
      var _data$product;

      return (data === null || data === void 0 ? void 0 : (_data$product = data.product) === null || _data$product === void 0 ? void 0 : _data$product.sku) === (item === null || item === void 0 ? void 0 : item.sku);
    });
  }, [downloadImages]);

  if (!item) {
    return React.createElement(GalleryItemShimmer, {
      classes: classes
    });
  }

  var productUrlSuffix = storeConfig && storeConfig.product_url_suffix;
  var closeModal = useCallback(function () {
    return setModalData(null);
  });
  var isSignInModalOpen = modalData === 'SIGN_IN';
  var isMiddleDialogOpen = modalData === 'MIDDLE_DIALOG';
  var wishlistButton = wishlistButtonProps ? React.createElement(WishlistGalleryButton, _extends({}, wishlistButtonProps, {
    product: item,
    setModalData: setModalData,
    isWishlistDialogOpen: isWishlistDialogOpen,
    setIsWishlistDialogOpen: setIsWishlistDialogOpen,
    isSmallIcon: true,
    activeItem: activeItem,
    setActiveItem: setActiveItem,
    isInFavorites: isInFavorites,
    refetchFavorites: refetchFavorites,
    isPlp: true
  })) : null; // btn is always shown when id of product item is equal with collectio id

  var isCollectiondSliderVisible = collectionVisible;
  var buttonText = crosssell_products_loading ? 'LOADING COLLECTION' : isCollectiondSliderVisible ? ' HIDE COLLECTION' : 'SHOW COLLECTION';
  var isCollectionBtnVisible = isSmallDevice ? true : false;

  if (isCollectiondSliderVisible) {
    isCollectionBtnVisible = true;
  } else if (isHoveredItem) {
    isCollectionBtnVisible = true;
  }

  var showCollectionButton = useMemo(function () {
    var _collectionItems, _collectionItems2;

    var collectionItems = item === null || item === void 0 ? void 0 : item.crosssell_products;
    collectionItems = (_collectionItems = collectionItems) === null || _collectionItems === void 0 ? void 0 : _collectionItems.filter(function (item) {
      return !isOutletSku(item === null || item === void 0 ? void 0 : item.sku);
    });
    return !isOutletProduct && isCollectionBtnVisible && ((_collectionItems2 = collectionItems) === null || _collectionItems2 === void 0 ? void 0 : _collectionItems2.length) > 0;
  }); // TODO handle latter is downloaded or not

  var downloadBtnClasses = isDownload ? classes.downloadedBtn : classes.downloadBtn;
  var name = item.name,
      price_range = item.price_range,
      small_image = item.small_image,
      url_key = item.url_key,
      sku = item.sku,
      url = item.url; // const { url: smallImageURL } = small_image;
  //Temporary solution for image url - live klevu problem

  var smallImageURL = small_image !== null && small_image !== void 0 && small_image.url.includes('.com/media/catalog/product/') ? small_image === null || small_image === void 0 ? void 0 : small_image.url : small_image === null || small_image === void 0 ? void 0 : small_image.url.replace('.com/', '.com/media/catalog/product/'); // ?? small_image; // UNCOMMENT FOR CF
  // const smallImageURL = imageItem?.image?.url ?? small_image?.url; // UNCOMMENT FOR CF

  var urlPath = url ? url : url_key; // const productLink = resourceUrl(`/${url_key}${productUrlSuffix || ''}`);

  var productLink = resourceUrl("/".concat(urlPath).concat(productUrlSuffix || '')); // priceRange contains GQL-computed outlet data { sku, price_range: { product_min_price, product_max_price } }
  // Falls back to Klevu item.price_range { maximum_price: { regular_price }, minimum_price: { regular_price } }

  var outletPriceData = priceRange === null || priceRange === void 0 ? void 0 : priceRange.price_range;
  var showDiscountedPrice = !!outletPriceData && parseFloat(outletPriceData === null || outletPriceData === void 0 ? void 0 : (_outletPriceData$prod = outletPriceData.product_min_price) === null || _outletPriceData$prod === void 0 ? void 0 : (_outletPriceData$prod2 = _outletPriceData$prod.regular_price) === null || _outletPriceData$prod2 === void 0 ? void 0 : _outletPriceData$prod2.value) > parseFloat(outletPriceData === null || outletPriceData === void 0 ? void 0 : (_outletPriceData$prod3 = outletPriceData.product_min_price) === null || _outletPriceData$prod3 === void 0 ? void 0 : (_outletPriceData$prod4 = _outletPriceData$prod3.final_price) === null || _outletPriceData$prod4 === void 0 ? void 0 : _outletPriceData$prod4.value);
  var itemDimensions = getDimensions(item);
  return React.createElement(React.Fragment, null, React.createElement("div", {
    className: isCollectiondSliderVisible ? classes.root_active : classes.root,
    "aria-live": "polite",
    "aria-busy": "false",
    onMouseEnter: function onMouseEnter() {
      return setIsHoveredItem(true);
    },
    onMouseLeave: function onMouseLeave() {
      return setIsHoveredItem(false);
    },
    ref: productItemRef
  }, React.createElement("div", {
    className: classes.content
  }, isUserSignedIn ? React.createElement("div", {
    className: loading ? classes.center : downloadBtnClasses
  }, loading ? React.createElement(LoadingIndicator, null, React.createElement(FormattedMessage, {
    id: 'addingToDownloads',
    defaultMessage: 'Loading...'
  })) : React.createElement(React.Fragment, null, React.createElement("div", {
    className: classes.mainDownloadImgWrapper
  }, !isInDownload ? React.createElement("div", {
    className: isFiltersHidden ? "".concat(classes.tooltipWrapper, " ").concat(classes.tooltipPosition) : classes.tooltipWrapper
  }, "Download product image") : React.createElement(React.Fragment, null, downloadStatus.remove && React.createElement("div", {
    className: isFiltersHidden ? "".concat(classes.tooltipWrapperCompleted, " ").concat(classes.tooltipPosition) : classes.tooltipWrapperCompleted
  }, "Remove from downloads"), downloadStatus.errorRemoving && React.createElement("div", {
    className: isFiltersHidden ? "".concat(classes.tooltipWrapperErrorRemoving, " ").concat(classes.tooltipPosition) : classes.tooltipWrapperErrorRemoving
  }, "Error removing image from download"), downloadStatus.successAdding && React.createElement("div", {
    className: isFiltersHidden ? "".concat(classes.tooltipWrapperSuccess, " ").concat(classes.tooltipPosition) : classes.tooltipWrapperSuccess
  }, "Successfully added to downloads"), downloadStatus.errorAdding && React.createElement("div", {
    className: isFiltersHidden ? "".concat(classes.tooltipWrapperErrorAdding, " ").concat(classes.tooltipPosition) : classes.tooltipWrapperErrorAdding
  }, "Error adding image for download")), React.createElement("div", {
    onClick: function onClick() {
      return isInDownload ? removeItemFromDownload(item === null || item === void 0 ? void 0 : item.sku) : addItemToDownload(item === null || item === void 0 ? void 0 : item.sku);
    }
  }, React.createElement("img", {
    src: !isInDownload ? DownloadImage : DownloadImageFull,
    alt: "download-image",
    className: classes.downloadImgProduct
  }))))) : null, isOutletProduct ? null : React.createElement("div", {
    className: classes.addFavouriteBtn
  }, wishlistButton, " "), React.createElement(Link, {
    to: productLink,
    onClick: function onClick() {
      handleLinkClick();
      persistScrollPosition === null || persistScrollPosition === void 0 ? void 0 : persistScrollPosition(item.id);
    },
    className: classes.images,
    "aria-label": "Go to product ".concat(name)
  }, React.createElement(Image, {
    alt: name,
    classes: {
      image: classes.image,
      loaded: classes.imageLoaded,
      notLoaded: classes.imageNotLoaded,
      container: classes.imageContainer
    },
    resource: smallImageURL,
    height: IMAGE_HEIGHT,
    widths: IMAGE_WIDTHS
  })), React.createElement("div", {
    className: "pl-1.5 pr-3 pb-3 pt-3 md_p-5 flex-1 flex flex-col justify-start"
  }, React.createElement("div", {
    className: "mt-1"
  }, React.createElement(Link, {
    to: productLink,
    onClick: function onClick() {
      handleLinkClick();
      persistScrollPosition === null || persistScrollPosition === void 0 ? void 0 : persistScrollPosition(item.id);
    },
    className: classes.name,
    "aria-label": "Go to product ".concat(name),
    title: name
  }, React.createElement("span", null, name))), React.createElement("div", null, React.createElement("div", {
    className: classes.skuContent
  }, React.createElement("p", {
    className: "leading-2"
  }, "Base Item ", sku), React.createElement("p", {
    className: "leading-2"
  }, itemDimensions.replace(/ x $/, '').replace('null" x', '').replace('x null"', ''))), React.createElement("div", {
    className: classes.price
  }, React.createElement("div", null, !!outletPriceData && React.createElement(React.Fragment, null, showDiscountedPrice && React.createElement(Price, {
    classes: {
      root: 'text-base leading-2 md_leading-171 font-normal text-black line-through inline mr-2'
    },
    value: parseFloat(outletPriceData === null || outletPriceData === void 0 ? void 0 : (_outletPriceData$prod5 = outletPriceData.product_min_price) === null || _outletPriceData$prod5 === void 0 ? void 0 : (_outletPriceData$prod6 = _outletPriceData$prod5.regular_price) === null || _outletPriceData$prod6 === void 0 ? void 0 : _outletPriceData$prod6.value),
    currencyCode: outletPriceData === null || outletPriceData === void 0 ? void 0 : (_outletPriceData$prod7 = outletPriceData.product_min_price) === null || _outletPriceData$prod7 === void 0 ? void 0 : (_outletPriceData$prod8 = _outletPriceData$prod7.regular_price) === null || _outletPriceData$prod8 === void 0 ? void 0 : _outletPriceData$prod8.currency
  }), React.createElement(Price, {
    classes: {
      root: 'text-base leading-2 md_leading-171 font-normal' + (showDiscountedPrice ? ' text-red inline' : '')
    },
    value: parseFloat(outletPriceData === null || outletPriceData === void 0 ? void 0 : (_outletPriceData$prod9 = outletPriceData.product_min_price) === null || _outletPriceData$prod9 === void 0 ? void 0 : (_outletPriceData$prod10 = _outletPriceData$prod9.final_price) === null || _outletPriceData$prod10 === void 0 ? void 0 : _outletPriceData$prod10.value),
    currencyCode: outletPriceData === null || outletPriceData === void 0 ? void 0 : (_outletPriceData$prod11 = outletPriceData.product_min_price) === null || _outletPriceData$prod11 === void 0 ? void 0 : (_outletPriceData$prod12 = _outletPriceData$prod11.final_price) === null || _outletPriceData$prod12 === void 0 ? void 0 : _outletPriceData$prod12.currency
  })), !outletPriceData && React.createElement(Price, {
    classes: {
      root: 'text-base leading-2 md_leading-171 font-normal'
    },
    value: parseFloat(price_range === null || price_range === void 0 ? void 0 : (_price_range$maximum_ = price_range.maximum_price) === null || _price_range$maximum_ === void 0 ? void 0 : (_price_range$maximum_2 = _price_range$maximum_.regular_price) === null || _price_range$maximum_2 === void 0 ? void 0 : _price_range$maximum_2.value),
    currencyCode: price_range === null || price_range === void 0 ? void 0 : (_price_range$maximum_3 = price_range.maximum_price) === null || _price_range$maximum_3 === void 0 ? void 0 : (_price_range$maximum_4 = _price_range$maximum_3.regular_price) === null || _price_range$maximum_4 === void 0 ? void 0 : _price_range$maximum_4.currency
  }), React.createElement("div", {
    className: classes.flagWrapper
  }, (item === null || item === void 0 ? void 0 : item.hf_new_designs) === 'Yes' && React.createElement("span", {
    className: classes.flag
  }, "New"), (item === null || item === void 0 ? void 0 : item.hf_designer_preference) === 'Yes' && React.createElement("span", {
    className: classes.flag
  }, "Designer's Choice"), (item === null || item === void 0 ? void 0 : item.hf_best_seller) === 'Yes' && React.createElement("span", {
    className: classes.flag
  }, "Best Seller"))))), React.createElement(OutletTags, {
    sku: sku,
    isFinalCall: !!(priceRange !== null && priceRange !== void 0 && priceRange.is_final_call),
    className: classes.outletTags
  }))), isCollectionBtnAllowed && React.createElement("div", {
    className: classes.actionsContainer
  }, showCollectionButton && React.createElement(Button, {
    priority: "low",
    fullWidth: true,
    size: "small",
    classes: {
      root: classes.btnRoot,
      content: classes.contentBtn
    },
    onClick: function onClick() {
      return collectionVisible ? toggleCollection(item, index) : handleSubmit(item === null || item === void 0 ? void 0 : item.sku);
    },
    "aria-label": buttonText
  }, buttonText))), activeItem && React.createElement(SignInModal, {
    isOpen: isSignInModalOpen,
    item: (activeItem === null || activeItem === void 0 ? void 0 : activeItem.sku) === (item === null || item === void 0 ? void 0 : item.sku) && item,
    closeModal: closeModal,
    itemOption: wishlistButtonProps === null || wishlistButtonProps === void 0 ? void 0 : wishlistButtonProps.item
  }), isMiddleDialogOpen && React.createElement(MiddleDialog, {
    setIsWishlistDialogOpen: setIsWishlistDialogOpen,
    autoClose: closeModal
  }));
};

ProductItem.propTypes = {
  classes: shape({
    image: string,
    imageLoaded: string,
    imageNotLoaded: string,
    imageContainer: string,
    images: string,
    name: string,
    price: string,
    root: string,
    content: string,
    actionsContainer: string,
    skuContent: string
  }),
  item: shape({
    uid: string,
    name: string.isRequired,
    small_image: shape({
      url: string.isRequired
    }),
    stock_status: string.isRequired,
    // type_id: string.isRequired,
    url_key: string.isRequired,
    url_suffix: string,
    sku: string.isRequired,
    price_range: shape({
      maximum_price: shape({
        regular_price: shape({
          value: number.isRequired,
          currency: string.isRequired
        }).isRequired
      }).isRequired
    }).isRequired
  }),
  storeConfig: shape({
    magento_wishlist_general_is_enabled: string.isRequired,
    product_url_suffix: string
  }),
  isCollectionBtnAllowed: bool // describes is show collection button allowed

};
export default ProductItem;